"use client";

import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import { BlogFilterStickyAside } from "./blog-filter-sticky-aside";
import type { BlogCategoryFilter, BlogListingLabels } from "./data";

const categoriesParser = parseAsArrayOf(parseAsString)
  .withOptions({ shallow: false, history: "replace" })
  .withDefault([]);

export function useSelectedCategories() {
  const [selected] = useQueryState("categories", categoriesParser);
  return selected;
}

export function BlogFilterRail({
  filters,
  totalCount,
  labels,
  className,
}: {
  filters: BlogCategoryFilter[];
  /** Total post count shown on the default "All Posts" item. */
  totalCount: number;
  labels: BlogListingLabels;
  className?: string;
}) {
  const [selected, setSelected] = useQueryState("categories", categoriesParser);

  const hasSelection = selected.length > 0;

  async function toggleCategory(id: string) {
    if (selected.includes(id)) {
      await setSelected((prev) => {
        const next = prev.filter((item) => item !== id);
        return next.length ? next : null;
      });
    } else {
      await setSelected((prev) => [...prev, id]);
    }
  }

  async function reset() {
    await setSelected(null);
  }

  // One model drives both the mobile tab strip and the desktop sidebar.
  const items = [
    {
      key: "__all",
      label: `${labels.allPosts} (${totalCount})`,
      ariaLabel: labels.allPosts,
      checked: !hasSelection,
      onToggle: reset,
    },
    ...filters.map(({ slug, label, count }) => ({
      key: slug,
      label: `${label} (${count})`,
      ariaLabel: label,
      checked: selected.includes(slug),
      onToggle: () => toggleCategory(slug),
    })),
  ];

  const clearButton = (
    <button
      type="button"
      onClick={reset}
      disabled={!hasSelection}
      className={cn(
        "text-ds-label-md cursor-pointer transition-colors",
        hasSelection
          ? "text-grey-600 hover:text-grey-950"
          : "text-grey-300 cursor-not-allowed"
      )}
    >
      {labels.clearAll}
    </button>
  );

  return (
    <>
      {/* Mobile — horizontal scrolling tab strip (Figma 5605:43823). */}
      <div className="lg:hidden">
        <div className="border-grey-100 flex items-center justify-between border-b px-6 py-4">
          <span className="text-ds-label-md text-grey-950">
            {labels.filters}
          </span>
          {clearButton}
        </div>
        <div className="relative">
          <div className="flex gap-2 overflow-x-auto px-6 py-5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {items.map((item) => (
              <label
                key={item.key}
                className="bg-grey-50 rounded-ds-m flex h-8 shrink-0 cursor-pointer items-center gap-2 px-2"
              >
                <Checkbox
                  checked={item.checked}
                  onCheckedChange={item.onToggle}
                  aria-label={item.ariaLabel}
                />
                <span className="text-ds-label-sm text-grey-950 whitespace-nowrap select-none">
                  {item.label}
                </span>
              </label>
            ))}
          </div>
          {/* Right-edge fade hinting the strip scrolls (Figma 5605:43664). */}
          <div
            aria-hidden="true"
            className="from-grey-0/0 to-grey-0 pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-r"
          />
        </div>
      </div>

      {/* Desktop — vertical sidebar (Figma 4392:12075). The blog-post box
          frames this column entirely — the page rail (left), the rows-column
          divider (right), the grid top border and, far below, the grid bottom
          border. So the card carries no border of its own (only its internal
          header divider); a card border here would double the box lines. */}
      <aside
        className={cn("hidden h-full w-full lg:block lg:w-[217px]", className)}
      >
        <BlogFilterStickyAside className="w-full">
          <div className="border-grey-100 flex items-center justify-between border-b px-4 py-4">
            <span
              className="text-ds-label-md text-grey-950"
              style={{ letterSpacing: "-0.02em" }}
            >
              {labels.filters}
            </span>
            {clearButton}
          </div>

          <ul className="flex flex-col gap-y-2 p-3">
            {items.map((item) => (
              <li key={item.key}>
                <label
                  className={cn(
                    "rounded-ds-m hover:bg-grey-50 flex h-8 cursor-pointer items-center gap-2 px-2 py-2 transition-colors",
                    item.checked && "bg-grey-50"
                  )}
                >
                  <Checkbox
                    checked={item.checked}
                    onCheckedChange={item.onToggle}
                    aria-label={item.ariaLabel}
                  />
                  <span className="text-ds-label-sm text-grey-950 select-none">
                    {item.label}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </BlogFilterStickyAside>
      </aside>
    </>
  );
}
